// JURÍDICO — wrappers das actions jur_*.
//
// Entra pelo mesmo `POST /comercial/propostas-geradas` das actions exp_* e
// seg_*: o roteador genérico por `body.action`. Criar recurso novo no API
// Gateway é ritual manual (recurso + método + integração + OPTIONS + permissão
// + deploy do stage), cujo esquecimento vira 403 silencioso.
//
// 🚨 Campos de contrato de tipo SENSÍVEL (vínculo de pessoa) chegam `null` para
//    quem não participa — `visivel` é o que diz se dá para confiar neles. É o
//    mesmo contrato de `participo` na listagem de expedientes.
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';

export type OrigemContrato = 'redigido' | 'terceiro';
export type SituacaoContrato = 'rascunho' | 'vigente' | 'encerrado' | 'rescindido';
export type SituacaoCalc = SituacaoContrato | 'vencido' | 'vencendo' | 'sem_vigencia';
export type Renovacao = 'nao' | 'automatica' | 'mediante_aviso';
export type NossoPapel = 'contratada' | 'contratante' | 'parte';

/** DISTRATO (2026-08-26): a ficha filha que encerra um contrato. As duas
 *  pontas vêm na mesma listagem — o contrato traz `distrato_id`/`distrato_em`,
 *  o distrato traz `contrato_pai_id`/`pai_codigo`. */
export type Instrumento = 'contrato' | 'distrato';

export interface ContratoLinha {
  id: number;
  origem: OrigemContrato;
  instrumento: Instrumento;
  contrato_pai_id: number | null;
  /** Só no distrato: a partir de quando o contrato pai deixa de valer. */
  data_efeito: string | null;
  /** Só no contrato: o distrato dele, se existir. */
  distrato_id: number | null;
  distrato_em: string | null;
  pai_codigo: string | null;
  pai_contraparte_nome: string | null;
  expediente_id: string | null;
  documento_id: number | null;
  codigo: string | null;
  tipo_nome: string | null;
  tipo_sensivel: boolean;
  visivel: boolean;
  situacao: SituacaoContrato;
  situacao_calc: SituacaoCalc;
  dias_para_vencer: number | null;
  status_expediente: string | null;
  assinatura_status: string | null;
  criado_em: string;
  // Podados quando `visivel` é false:
  titulo: string | null;
  contraparte_nome: string | null;
  contraparte_doc: string | null;
  objeto: string | null;
  valor: string | null;         // numeric vira string em node-pg
  nosso_papel: NossoPapel | null;
  vigencia_inicio: string | null;
  vigencia_fim: string | null;
  renovacao: Renovacao | null;
  reajuste_indice: string | null;
  projeto_numero: string | null;
  projeto_titulo: string | null;
  // TIPO (2026-09-15) — do catálogo, não do contrato: não são podados.
  tipo_id: number | null;
  tipo_codigo: string | null;
  /** Nome, NESTE tipo, do lado 'contratante' (ex.: Mutuante). null = Contratante. */
  rotulo_contratante: string | null;
  /** Nome, NESTE tipo, do lado 'contratada' (ex.: Mutuário). null = Contratada. */
  rotulo_contratada: string | null;
}

async function chamar<T = any>(action: string, body: Record<string, any> = {}): Promise<T> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
    { action, ...body }, { headers: { Authorization: token } });
  return r.data.body ? JSON.parse(r.data.body) : r.data;
}

export const listarContratos = (f: {
  origem?: string; situacao?: string; busca?: string; vencendo_em_dias?: number;
} = {}) => chamar<{
  contratos: ContratoLinha[]; pode_criar: boolean; pode_editar: boolean; pode_excluir: boolean;
  pode_gerenciar_tipos?: boolean;
}>('jur_listar', f);

export const obterContrato = (id: number) =>
  // `cobertura` (guarda-chuva): os contratos que este instrumento cobre.
  chamar<{ contrato: any; cobertura: LinhaCobertura[] }>('jur_obter', { id });

/** Cria o expediente E a ficha juntos, e devolve o id do expediente para abrir
 *  o editor (que é o mesmo dos Expedientes — a Pam inclusive). */
export const criarContrato = (p: Record<string, any>) =>
  chamar<{ id: number; expediente_id: string }>('jur_criar', p);

/** O PDF já subiu pelo acervo; aqui só se cria a ficha em cima dele. */
export const cadastrarTerceiro = (p: Record<string, any>) =>
  chamar<{ id: number }>('jur_terceiro', p);

export const salvarFicha = (p: Record<string, any>) =>
  chamar<{ id: number; atualizado: boolean }>('jur_salvar', p);

// ── Contrato assinado que chega em PDF (2026-08-21) ────────────────────────
// O arquivo sobe pela MESMA porta do acervo (`subirArquivo`, categoria
// `contratos`) — é lá que ele vai morar no fim. Depois a leitura é assíncrona:
// `lerContrato` só abre a leitura, e a tela pergunta "já leu?" até responder.
// Bedrock sobre 30 páginas não cabe nos 29 s do API Gateway.
export interface LeituraContrato {
  id: number;
  status: 'processando' | 'pronto' | 'erro';
  campos: Record<string, any>;
  sumario_md: string | null;
  /** 'texto' = PDF com texto · 'imagem' = digitalizado · 'docx' = Word. */
  fonte: 'texto' | 'imagem' | 'docx' | null;
  erro: string | null;
  arquivo_nome: string | null;
  documento_id: number | null;
  contrato_id: number | null;
}

export const lerContrato = (p: {
  s3_key: string; arquivo_nome?: string;
  /** 'distrato' troca o que a IA procura: data de efeito, motivo e quitação —
   *  vigência e reajuste não existem nesse papel. 'modelo' devolve o TEXTO do
   *  contrato com os dados em branco, para o cadastro do tipo. */
  finalidade?: 'contrato' | 'distrato' | 'modelo';
}) => chamar<{ leitura_id: number; status: string }>('jur_ler', p);

export const statusLeitura = (id: number) =>
  chamar<{ leitura: LeituraContrato }>('jur_leitura', { id });

/** Arquiva o PDF no acervo E cria a ficha, com o que a PESSOA confirmou. */
export const acervarContrato = (p: Record<string, any>) =>
  chamar<{ contrato_id: number; documento_id: number }>('jur_acervar', p);

/** Link assinado do PDF do contrato de terceiro/venda.
 *
 *  Passa pela rota do ACERVO (é lá que o arquivo mora), e não por uma porta
 *  nova no Jurídico: o gate do documento é o do acervo, e duplicá-lo aqui
 *  criaria duas regras de acesso para o mesmo arquivo. Desde 21/08 a categoria
 *  `contratos` é restrita à permissão do Jurídico — quem não tem recebe 404,
 *  como qualquer documento restrito. */
export async function abrirPdfDoContrato(documento_id: number): Promise<string> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/documentos`,
    { action: 'get', id: documento_id, com_download: true },
    { headers: { Authorization: token } });
  const d = r.data.body ? JSON.parse(r.data.body) : r.data;
  if (!d.download_url) throw new Error('O acervo não devolveu o link do arquivo.');
  return d.download_url;
}

/** O PDF do contrato DO PROJETO, para quem trabalha nele (2026-09-07).
 *
 *  🚨 Não dá para reusar `abrirPdfDoContrato` aqui. Ele vai pela rota do
 *  ACERVO, que exige a permissão de lá e responde **404** para a categoria
 *  `contratos` (restrita ao Jurídico desde 21/08). O gestor e o coordenador,
 *  que passaram a ver o card, clicariam em "Abrir PDF" e não abririam nada.
 *
 *  Esta porta é do Jurídico e o gate dela é a COBERTURA: só sai o contrato que
 *  cobre um projeto ao qual a pessoa está vinculada. Projeto alheio responde
 *  404, nunca 403 — pedir o PDF de um projeto que não é o seu não pode
 *  confirmar que ele existe. */
export async function abrirPdfDoProjeto(contrato_op_id: string): Promise<string> {
  const d = await chamar<{ download_url: string | null }>(
    'jur_pdf_do_projeto', { contrato_op_id });
  if (!d.download_url) throw new Error('O acervo não devolveu o link do arquivo.');
  return d.download_url;
}

/** Sobe o PDF do contrato assinado pela porta do JURÍDICO (2026-09-07).
 *
 *  🚨 O `subirArquivo` do acervo barra o coordenador no PRIMEIRO passo: lá o
 *  porteiro é a permissão de VER a categoria (`juridico.contratos.ver`, o
 *  módulo inteiro), e quem não a tem nem consegue pedir a URL de upload.
 *  Alargar aquele código seria entregar a gaveta toda.
 *
 *  Aqui o gate é o de ANEXAR e a categoria é fixa no servidor. */
export async function subirContratoAssinado(arquivo: File) {
  const d = await chamar<{ upload_url: string; s3_key: string }>('jur_upload_url', {
    filename: arquivo.name, content_type: arquivo.type || 'application/pdf',
  });
  const r = await fetch(d.upload_url, {
    method: 'PUT', body: arquivo,
    headers: { 'Content-Type': arquivo.type || 'application/pdf' },
  });
  if (!r.ok) throw new Error('Falha ao enviar o arquivo.');
  return d.s3_key;
}

// ── Assinatura do contrato = assinatura da venda (2026-08-31) ──────────────
// O modal abre com quem assina pela Hydrodata (representante legal) e pela
// outra parte (contato do cliente da ficha), qualificados para o carimbo.
export interface SugestaoAssinaturaContrato {
  signatarios: any[];
  avisos: string[];
  nosso_papel: string;
  /** Papéis que o servidor EXIGE na lista de quem assina, pelos nomes da ficha
   *  (Contratante/Contratada, Mutuante/Mutuária, Parte/Parte). 16/09/2026. */
  papeis_obrigatorios?: string[];
}
export const sugerirSignatariosContrato = (expediente_id: string) =>
  chamar<SugestaoAssinaturaContrato>('jur_sugerir_signatarios', { expediente_id });

// ── Templates de contrato (minutas da casa) ────────────────────────────────
export interface TemplateContrato {
  id: number; nome: string; tipo_id: number | null;
  tipo_prefixo: string | null; tipo_nome: string | null;
  criado_por: string; criado_em: string; atualizado_em: string | null;
  tamanho: number;
}
export const listarTemplates = () => chamar<{ templates: TemplateContrato[] }>('jur_templates');
/** Mesmo nome ATUALIZA o template — a minuta da casa evolui, não acumula cópias. */
export const salvarTemplate = (p: { nome: string; expediente_id: string }) =>
  chamar<{ id: number; nome: string; atualizado: boolean }>('jur_template_salvar', p);
export const excluirTemplate = (id: number) =>
  chamar<{ excluido: boolean }>('jur_template_excluir', { id });

export const excluirContrato = (id: number) => chamar<{ excluido: boolean }>('jur_excluir', { id });

/** Registra o distrato de um contrato. Duas portas: `documento_id` (PDF já
 *  arquivado) ou `tipo_id` (redigir aqui, com numeração e assinatura).
 *  O contrato pai não é alterado — quem passa a dizer "rescindido" é a view. */
export const distratarContrato = (p: {
  contrato_id: number; data_efeito: string;
  documento_id?: number; tipo_id?: number;
  objeto?: string | null; valor?: number | null; observacoes?: string | null;
}) => chamar<{ id: number; contrato_id: number; expediente_id?: string }>('jur_distratar', p);

// ── Tipos de contrato (2026-09-15) ─────────────────────────────────────────
// Um catálogo só para redigir e para subir o PDF assinado. Quem tem
// `juridico.tipos.gerenciar` cadastra (mútuo, locação…) e dá nome às partes.
export interface TipoContrato {
  id: number;
  codigo: string;
  prefixo: string;
  nome: string;
  descricao: string | null;
  sensivel: boolean;
  ativo: boolean;
  ordem: number;
  /** Aparece em "Redigir contrato". false = só entra por upload (seguro). */
  redigivel: boolean;
  rotulo_contratante: string | null;
  rotulo_contratada: string | null;
  subcategoria_codigo: string | null;
  /** Só para quem gerencia o catálogo. */
  corpo_padrao_html?: string | null;
  em_uso?: number | null;
}

/** Tipo de fluxo próprio: não aparece em "Redigir contrato" nem no upload comum. */
export const TIPO_DISTRATO = 'distrato';

export const listarTiposContrato = (p: { incluir_inativos?: boolean } = {}) =>
  chamar<{ tipos: TipoContrato[]; pode_gerenciar: boolean }>('jur_tipos', p);

export const salvarTipoContrato = (p: Partial<TipoContrato> & {
  confirmar_rotulos?: boolean; confirmar_sigilo?: boolean;
}) => chamar<{ tipo: TipoContrato; criado?: boolean; atualizado?: boolean }>('jur_tipo_salvar', p);

/** Os nomes das duas partes de um tipo (ou de uma linha da lista). */
export function rotulosPartes(t?: { rotulo_contratante?: string | null; rotulo_contratada?: string | null } | null) {
  return {
    contratante: t?.rotulo_contratante || 'Contratante',
    contratada: t?.rotulo_contratada || 'Contratada',
  };
}

/** Opções do "Nosso papel" para um tipo. Tipo com os nomes padrão mantém os
 *  textos de sempre; tipo com nomes próprios diz "Somos Mutuante". */
export function opcoesPapel(t?: { rotulo_contratante?: string | null; rotulo_contratada?: string | null } | null) {
  if (!t?.rotulo_contratante && !t?.rotulo_contratada) {
    return (Object.entries(PAPEL_CFG) as [NossoPapel, string][]).map(([value, label]) => ({ value, label }));
  }
  const r = rotulosPartes(t);
  return [
    { value: 'contratante' as NossoPapel, label: `Somos ${r.contratante}` },
    { value: 'contratada' as NossoPapel, label: `Somos ${r.contratada}` },
    { value: 'parte' as NossoPapel, label: 'Somos parte' },
  ];
}

export function textoPapel(papel: NossoPapel | null, t?: { rotulo_contratante?: string | null; rotulo_contratada?: string | null } | null) {
  if (!papel) return null;
  return opcoesPapel(t).find((o) => o.value === papel)?.label || PAPEL_CFG[papel] || papel;
}

// ─── COBERTURA (guarda-chuva, 2026-08-25) ───────────────────────────────────
// Um instrumento assinado pode cobrir vários contratos do sistema: o contrato
// da Nova Engevix chegou meses depois citando 5 propostas, que aqui são o
// contrato-pai e os aditivos dele.
export interface ContratoOpOpcao {
  id: string;
  numero: string;
  titulo: string;
  status: string;
  contrato_pai_id: string | null;
  pai_numero: string | null;
  valor_contratado: string | number | null;
  data_inicio_prevista: string | null;
  data_termino_previsto: string | null;
  /** id da ficha que JÁ cobre este contrato (null = livre). */
  coberto_por: number | null;
  /** contraparte da ficha que cobre — null quando a ficha é sensível. */
  coberto_por_nome: string | null;
}

/** Os contratos do cliente, para escolher o que o instrumento cobre. */
export const listarContratosOpDoCliente = (cliente_id: number) =>
  chamar<{ contratos: ContratoOpOpcao[] }>('jur_contratos_do_cliente', { cliente_id });

export interface LinhaCobertura {
  contrato_op_id: string;
  numero: string;
  titulo: string | null;
  status: string | null;
  principal: boolean;
}

export const SITUACAO_CFG: Record<string, { label: string; cor: string }> = {
  rascunho:     { label: 'Rascunho',     cor: 'default' },
  vigente:      { label: 'Vigente',      cor: 'green' },
  vencendo:     { label: 'Vencendo',     cor: 'orange' },
  vencido:      { label: 'Vencido',      cor: 'red' },
  sem_vigencia: { label: 'Sem vigência', cor: 'default' },
  encerrado:    { label: 'Encerrado',    cor: 'default' },
  rescindido:   { label: 'Rescindido',   cor: 'red' },
};

export const RENOVACAO_CFG: Record<Renovacao, string> = {
  nao: 'Não renova',
  automatica: 'Renova sozinho',
  mediante_aviso: 'Renova mediante aviso',
};

export const PAPEL_CFG: Record<NossoPapel, string> = {
  contratada: 'Somos a contratada',
  contratante: 'Somos a contratante',
  parte: 'Somos parte',
};
